import { IGirafeContext } from '@geogirafe/lib-geoportal/core';
import WFS from 'ol/format/WFS';
import * as olFormatFilter from 'ol/format/filter';
import Point from 'ol/geom/Point';
import { LiegtypselectObject, ReportServiceConfig, WfsServiceConfig } from '../../../bs/types';

export class Service {
  private context: IGirafeContext;
  private currentRef: string = '';
  private statusTimer: ReturnType<typeof setTimeout> | null = null;
  private abortController: AbortController | null = null;

  constructor(context: IGirafeContext) {
    this.context = context;
  }

  async getFeaturesForLiegenschaft(east: number, north: number): Promise<LiegtypselectObject[]> {
    const wfsFormat = new WFS();

    const point = new Point([Number(east), Number(north)]);

    const featureRequest = wfsFormat.writeGetFeature({
      featurePrefix: this.wfsConfig.featurePrefix,
      featureNS: this.wfsConfig.featureNS,
      featureTypes: [
        'BS_Liegenschaften_Grundstuecke_Liegenschaftsparzelle',
        'BS_Liegenschaften_Grundstuecke_Allmendparzelle',
        'BS_Liegenschaften_Grundstuecke_Unterbaurecht',
        'BS_Liegenschaften_Grundstuecke_Baurecht'
      ],
      filter: olFormatFilter.intersects('the_geom', point),
      srsName: this.wfsConfig.srsName
    });

    const requestBody = new XMLSerializer().serializeToString(featureRequest);

    try {
      const response = await fetch(this.wfsConfig.mapservUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml; charset=UTF-8'
        },
        body: requestBody
      });

      if (!response.ok) {
        throw new Error(`WFS request failed: ${response.statusText}`);
      }

      const textData = await response.text();
      const features = wfsFormat.readFeatures(textData);

      return features.map((feature) => ({
        typ: feature.get('bs_art') || '',
        sektion: feature.get('bs_sektionnummer') || '',
        parznr: feature.get('bs_prznummer') || '',
        egrid: feature.get('bs_egrid') || ''
      }));
    } catch (error) {
      console.error('Error fetching Liegenschaft features:', error);
      throw new Error('Fehler beim Abrufen der Liegenschaftsdaten');
    }
  }

  protected get wfsConfig() {
    return this.context.configManager.Config.extendedConfig!.wfsService as WfsServiceConfig;
  }

  protected get reportConfig() {
    return this.context.configManager.Config.extendedConfig!.reportService as ReportServiceConfig;
  }

  async getFeaturesFromWFS(east: number, north: number): Promise<boolean> {
    const wfsFormat = new WFS();

    const point = new Point([Number(east), Number(north)]);

    const featureRequest = wfsFormat.writeGetFeature({
      featurePrefix: this.wfsConfig.featurePrefix,
      featureNS: this.wfsConfig.featureNS,
      featureTypes: this.wfsConfig.featureTypes,
      filter: olFormatFilter.intersects('the_geom', point),
      srsName: this.wfsConfig.srsName
    });

    const requestBody = new XMLSerializer().serializeToString(featureRequest);

    try {
      const response = await fetch(this.wfsConfig.mapservUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml; charset=UTF-8'
        },
        body: requestBody
      });

      if (!response.ok) {
        throw new Error(`WFS request failed: ${response.statusText}`);
      }

      const textData = await response.text();
      const features = wfsFormat.readFeatures(textData);

      return features.length > 0;
    } catch {
      throw new Error('Error checking coordinates in EB_Zone');
    }
  }

  async createReport(layout: string, param: string): Promise<string> {
    this.cancelReport();
    const requestBody = {};

    const postUrl = `${this.reportConfig.baseUrl}/${this.reportConfig.endpoints.pdf}/${layout}/${param}`;
    this.abortController = new AbortController();
    const response = await fetch(postUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      signal: this.abortController.signal
    });

    if (!response.ok) {
      throw new Error(`Report creation failed: ${response.statusText}`);
    }

    const responseData = await response.json();
    this.currentRef = responseData.ref;

    return this.pollStatus();
  }

  private async pollStatus(): Promise<string> {
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        if (!this.currentRef) {
          return reject('No ref found');
        }

        const statusUrl = `${this.reportConfig.baseUrl}/${this.reportConfig.endpoints.status}/${this.currentRef}`;
        try {
          this.abortController = new AbortController();
          const resp = await fetch(statusUrl, { signal: this.abortController.signal });
          if (!resp.ok) {
            return reject(`Status check failed: ${resp.statusText}`);
          }

          const statusData = await resp.json();

          if (statusData.done) {
            if (statusData.status !== 'error') {
              const downloadUrl = `${this.reportConfig.baseUrl}/${this.reportConfig.endpoints.get}/${this.currentRef}`;
              this.clearAll();
              return resolve(downloadUrl);
            } else {
              this.clearAll();
              return reject('Report generation error (server-side)');
            }
          } else {
            this.statusTimer = setTimeout(checkStatus, this.reportConfig.pollingInterval);
          }
        } catch {
          this.clearAll();
          return reject('Fehler beim Erstellen des Dokumentes');
        }
      };
      checkStatus();
    });
  }

  cancelReport() {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
      this.statusTimer = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.currentRef && this.reportConfig) {
      const cancelUrl = `${this.reportConfig.baseUrl}/${this.reportConfig.endpoints.cancel}/${this.currentRef}`;
      fetch(cancelUrl, { method: 'DELETE' }).catch((err) => {
        console.warn('Error on cancel report', err);
      });
    }

    this.currentRef = '';
  }

  private clearAll() {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
      this.statusTimer = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.currentRef = '';
  }
}
